/* eslint-disable max-len */
// Section 1 - Plant & Production Information (subCategory 2)

import {Units} from '../../../../../components/Forms/Constants/CommonESGConstants/constants';
import {conditionType, inputType} from '../../../interface/inputType';

import {
  COUNTRY_WITH_CODE_OPTIONS,
  CBAM_GOOD_CATEGORY_OPTIONS,
  CN_CODE_OPTIONS,
  COUNTRY_OPTIONS,
  CURRENCY_OPTIONS,
  DATA_SOURCE_OPTIONS,
  ECONOMIC_ACTIVITY_OPTIONS,
  ELECTRICITY_CONNECTION_OPTIONS,
  EMISSION_FACTOR_SOURCE_OPTIONS,
  FORM_OF_PRICE_OPTIONS,
  JUSTIFICATION_FOR_DEFAULT_VALUES_OPTIONS,
  MAIN_REDUCTION_AGENT_OPTIONS,
  MEASURABLE_HEAT_OPTIONS,
  MONITORING_METHODOLOGY_OPTIONS,
  PREDOMINANT_METHOD_OPTIONS,
  PRODUCTION_ROUTE_OPTIONS,
  QUALITY_ASSURANCE_OPTIONS,
  SOURCE_STREAM_TYPE_OPTIONS,
  VERIFIER_BODY_OPTIONS,
  WASTE_GAS_USAGE_METHOD_OPTIONS,
  YES_NO_OPTIONS,
  GOODS_CATEGORY_OPTIONS,
  ROUTE_OPTIONS
} from './ironAndSteelOptions';
import {
  reportingPeriodKeys,
  installationLocationKeys,
  geographicCoordinatesKeys,
  economicActivityKeys,
  verificationKeys,
  processSetupKeys,
  precursorSetupKeys,
  authorizedRepresentativeKeys,
  ProductComplianceKeys,
  sourceStreamKeys,
  monitoringMethodologyKeys,
  activityDataKeys,
  fuelAllocationKeys,
  monitoringApproachKeys,
  electricityKeys,
  emissionFactorsAndCHPKeys,
  processIdentificationKeys,
  precursorIdentificationKeys,
  originKeys,
  emissionsKeys,
  dataVerificationKeys,
  carbonTaxStatusKeys,
  taxDetailsKeys,
  paymentKeys,
  coverageAndProofKeys,
  customerMRNNumberKeys,
  customerLogisticsKeys,
  chemicalCompositionKeys,
  productionKeys,
  fuelInputsKeys,
  energyBalanceKeys,
  wasteGasKeys,
  scrapAndElectricityKeys,
  carbonCaptureKeys
} from './ironandSteelQuestionsKeys';

/**
 * Generates Goods serial number options (G1, G2, G3, etc.) based on the number of entries
 * @param count - Number of Goods entries
 * @returns Array of option objects with value and label
 */
export const generateGoodsSerialNumberOptions = (
  count: number
): Array<{value: string; label: string}> => {
  if (!count || count <= 0) {
    return [];
  }

  return Array.from({length: count}, (_, index) => ({
    value: `G${index + 1}`,
    label: `G${index + 1}`
  }));
};

// SECTION 1

export const section1PlantAndProductionInformationTabQuestions = [
  {
    key: '1',
    label: 'Subsection 1.1 – Reporting Period',
    items: [
      {
        field: [
          {
            fieldType: inputType.DATEPICKER_FINANCIAL_YEAR,
            fieldName: reportingPeriodKeys.REPORTING_PERIOD_QUESTION_1_1_1,
            questionNo: '1.1.1',
            fieldLabel: 'Reporting Period Start',
            tooltip:
              'Enter the first date of the CBAM reporting period used for production & emissions calculation. Must match the quarter/year you report under CBAM transitional reporting.',
            colSpan: true,
            isRequired: true,
            financialYearValidation: true
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.DATEPICKER_FINANCIAL_YEAR,
            fieldName: reportingPeriodKeys.REPORTING_PERIOD_QUESTION_1_1_2,
            questionNo: '1.1.2',
            fieldLabel: 'Reporting Period End',
            tooltip:
              'Enter the last date of the reporting period. Ensure the same period is used across activity data, fuels, electricity, and production quantities.',
            colSpan: true,
            isRequired: true,
            financialYearValidation: true,
            relatedDateFieldName:
              reportingPeriodKeys.REPORTING_PERIOD_QUESTION_1_1_1
          }
        ]
      }
    ]
  },
  {
    key: '2',
    label: 'Subsection 1.2 – Installation Location',
    items: [
      {
        field: [
          {
            fieldType: inputType.TEXT,
            fieldName:
              installationLocationKeys.INSTALLATION_LOCATION_QUESTION_1_2_1,
            questionNo: '1.2.1',
            fieldLabel: 'Installation Name (Local)',
            tooltip:
              'Official installation name as per local registration/licenses. Must be consistent with invoices, permits, and exporter records.',
            isRequired: true,
            colSpan: true
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.TEXT,
            fieldName:
              installationLocationKeys.INSTALLATION_LOCATION_QUESTION_1_2_2,
            questionNo: '1.2.2',
            fieldLabel: 'Installation Name (English)',
            tooltip:
              'English name for CBAM/customs matching. Should match what you use in export documentation where possible.',
            colSpan: true,
            isRequired: true
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.TEXT,
            fieldName:
              installationLocationKeys.INSTALLATION_LOCATION_QUESTION_1_2_3,
            questionNo: '1.2.3',
            fieldLabel: 'Street, Number',
            tooltip:
              'Full physical address of the installation producing CBAM goods. Must be verifiable by documents or site records.',
            isRequired: true,
            colSpan: true
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.ALPHANUMERIC,
            fieldName:
              installationLocationKeys.INSTALLATION_LOCATION_QUESTION_1_2_4,
            questionNo: '1.2.4',
            fieldLabel: 'Postcode',
            tooltip:
              'Enter the official postal code of the installation location.',
            isRequired: true,
            colSpan: true
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.TEXT,
            fieldName:
              installationLocationKeys.INSTALLATION_LOCATION_QUESTION_1_2_5,
            questionNo: '1.2.5',
            fieldLabel: 'City',
            tooltip: 'City/district name where the installation is located.',
            isRequired: true,
            colSpan: true
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.SELECT,
            fieldName:
              installationLocationKeys.INSTALLATION_LOCATION_QUESTION_1_2_6,
            questionNo: '1.2.6',
            fieldLabel: 'Country',
            tooltip:
              'Select the country where production occurs (ISO 2-letter). Impacts electricity default factors and jurisdiction.',
            colSpan: true,
            options: COUNTRY_WITH_CODE_OPTIONS,
            isRequired: true
          }
        ]
      }
    ]
  },
  {
    key: '3',
    label: 'Subsection 1.3 – Geographic Coordinates',
    items: [
      {
        field: [
          {
            fieldType: inputType.TEXT,
            fieldName:
              geographicCoordinatesKeys.GEOGRAPHIC_COORDINATES_QUESTION_1_3_1,
            questionNo: '1.3.1',
            fieldLabel: 'UNLOCODE',
            tooltip:
              'Provide nearest UN/LOCODE used in trade logistics (port/location). Helps link to trade movements & customs chain.',
            isRequired: true,
            colSpan: true,
            textLimit: 5
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.NUMBER,
            fieldName:
              geographicCoordinatesKeys.GEOGRAPHIC_COORDINATES_QUESTION_1_3_2,
            questionNo: '1.3.2',
            fieldLabel: 'Latitude',
            tooltip:
              'GPS latitude of installation (main gate/stack). Used for location verification and consistency checks.',
            colSpan: true,
            isRequired: true,
            roundTo: 6
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.NUMBER,
            fieldName:
              geographicCoordinatesKeys.GEOGRAPHIC_COORDINATES_QUESTION_1_3_3,
            questionNo: '1.3.3',
            fieldLabel: 'Longitude',
            tooltip:
              'GPS longitude of installation (main gate/stack). Must match actual facility coordinates.',
            colSpan: true,
            isRequired: true,
            roundTo: 6
          }
        ]
      }
    ]
  },
  {
    key: '4',
    label: 'Subsection 1.4 – Economic Activity',
    items: [
      {
        field: [
          {
            fieldType: inputType.SELECT,
            fieldName: economicActivityKeys.ECONOMIC_ACTIVITY_QUESTION_1_4_1,
            questionNo: '1.4.1',
            fieldLabel: 'Economic Activity (NACE)',
            tooltip: 'Main economic activity code (NACE or equivalent).',
            colSpan: true,
            options: ECONOMIC_ACTIVITY_OPTIONS,
            isRequired: true
          }
        ]
      }
    ]
  },
  {
    key: '5',
    label: 'Subsection 1.5 – Verification',
    items: [
      {
        field: [
          {
            fieldType: inputType.RADIOGROUP,
            fieldName: verificationKeys.VERIFICATION_QUESTION_1_5_1,
            questionNo: '1.5.1',
            fieldLabel: 'Is data verified?',
            tooltip:
              'Choose Yes/No based on whether emissions data was verified by a third party. In definitive phase, verification becomes critical.',
            colSpan: true,
            options: ['Yes', 'No'],
            isRequired: true
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.TEXT,
            fieldName: verificationKeys.VERIFICATION_QUESTION_1_5_2,
            questionNo: '1.5.2',
            fieldLabel: 'Verifier: Company Name',
            tooltip:
              'Fill only if 1.5.1 = Yes. Enter legal name of verification body used for assurance of CBAM data.',
            colSpan: true,
            conditionalVisibility: {
              dependsOn: verificationKeys.VERIFICATION_QUESTION_1_5_1,
              showWhen: 'Yes',
              condition: conditionType.EQUALS
            },
            requiredOn: {
              fieldName: verificationKeys.VERIFICATION_QUESTION_1_5_1,
              condition: conditionType.EQUALS,
              values: ['Yes'],
              required: true
            }
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.TEXT,
            fieldName: verificationKeys.VERIFICATION_QUESTION_1_5_3,
            questionNo: '1.5.3',
            fieldLabel: 'Verifier: Address',
            tooltip:
              'Fill only if 1.5.1 = Yes. Address of verifiers office as per verification statement.',
            colSpan: true,
            conditionalVisibility: {
              dependsOn: verificationKeys.VERIFICATION_QUESTION_1_5_1,
              showWhen: 'Yes',
              condition: conditionType.EQUALS
            },
            requiredOn: {
              fieldName: verificationKeys.VERIFICATION_QUESTION_1_5_1,
              condition: conditionType.EQUALS,
              values: ['Yes'],
              required: true
            }
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.SELECT,
            fieldName: verificationKeys.VERIFICATION_QUESTION_1_5_4,
            questionNo: '1.5.4',
            fieldLabel: 'Verifier: Accreditation Body',
            tooltip:
              'Fill only if 1.5.1 = Yes. Select the EU accreditation authority/country recognizing the verifier.',
            colSpan: true,
            options: VERIFIER_BODY_OPTIONS,
            conditionalVisibility: {
              dependsOn: verificationKeys.VERIFICATION_QUESTION_1_5_1,
              showWhen: 'Yes',
              condition: conditionType.EQUALS
            },
            requiredOn: {
              fieldName: verificationKeys.VERIFICATION_QUESTION_1_5_1,
              condition: conditionType.EQUALS,
              values: ['Yes'],
              required: true
            }
          }
        ]
      }
    ]
  },
  {
    key: '6',
    label: 'Subsection 1.6 – Process Setup',
    items: [
      {
        field: [
          {
            fieldType: inputType.ADD_NEW_TO_TABLE,
            fieldName: processSetupKeys.PROCESS_SETUP_QUESTION_1_6_1_A,
            questionNo: '1.6.1 A',
            fieldLabel:
              'Aggregated goods categories and relevant production processes',
            tooltip:
              'List all distinct production processes/units producing CBAM goods. These drive Section 4 process-level reporting structure.',
            colSpan: true,
            isRequired: true,
            srNoPrefix: 'G',
            formList: {
              formFields: [
                {
                  type: inputType.SELECT,
                  fieldName: processSetupKeys.PROCESS_SETUP_QUESTION_1_6_2_A,
                  fieldLabel: 'Goods Category',
                  options: GOODS_CATEGORY_OPTIONS,
                  colSpan: true
                },
                {
                  type: inputType.SELECT,
                  fieldName: processSetupKeys.PROCESS_SETUP_QUESTION_1_6_3_A,
                  fieldLabel: 'Route1',
                  options: ROUTE_OPTIONS,
                  colSpan: true
                },
                {
                  type: inputType.SELECT,
                  fieldName: processSetupKeys.PROCESS_SETUP_QUESTION_1_6_4_A,
                  fieldLabel: 'Route2',
                  options: ROUTE_OPTIONS,
                  colSpan: true
                },
                {
                  type: inputType.SELECT,
                  fieldName: processSetupKeys.PROCESS_SETUP_QUESTION_1_6_5_A,
                  fieldLabel: 'Route3',
                  options: ROUTE_OPTIONS,
                  colSpan: true
                },
                {
                  type: inputType.SELECT,
                  fieldName: processSetupKeys.PROCESS_SETUP_QUESTION_1_6_6_A,
                  fieldLabel: 'Route4',
                  options: ROUTE_OPTIONS,
                  colSpan: true
                },
                {
                  type: inputType.SELECT,
                  fieldName: processSetupKeys.PROCESS_SETUP_QUESTION_1_6_7_A,
                  fieldLabel: 'Route5',
                  options: ROUTE_OPTIONS,
                  colSpan: true
                },
                {
                  type: inputType.SELECT,
                  fieldName: processSetupKeys.PROCESS_SETUP_QUESTION_1_6_8_A,
                  fieldLabel: 'Route6',
                  options: ROUTE_OPTIONS,
                  colSpan: true
                }
              ]
            }
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.ADD_NEW_TO_TABLE,
            fieldName: processSetupKeys.PROCESS_SETUP_QUESTION_1_6_1_B,
            questionNo: '1.6.1 B',
            fieldLabel: 'Relevant production processes',
            tooltip:
              'Select CBAM “Aggregated Good” category (Annex I/II mapping). Must match what is produced by that process.',
            colSpan: true,
            isRequired: true,
            srNoPrefix: 'P',
            formList: {
              formFields: [
                {
                  type: inputType.SELECT,
                  fieldName: processSetupKeys.PROCESS_SETUP_QUESTION_1_6_2_B,
                  fieldLabel: 'Production process',
                  colSpan: true
                },
                {
                  type: inputType.SELECT,
                  fieldName: processSetupKeys.PROCESS_SETUP_QUESTION_1_6_3_B,
                  fieldLabel: 'Route1',
                  dynamicOptionsFromTableA: true,
                  colSpan: true
                },
                {
                  type: inputType.SELECT,
                  fieldName: processSetupKeys.PROCESS_SETUP_QUESTION_1_6_4_B,
                  fieldLabel: 'Route2',
                  dynamicOptionsFromTableA: true,
                  colSpan: true
                },
                {
                  type: inputType.SELECT,
                  fieldName: processSetupKeys.PROCESS_SETUP_QUESTION_1_6_5_B,
                  fieldLabel: 'Route3',
                  dynamicOptionsFromTableA: true,
                  colSpan: true
                },
                {
                  type: inputType.SELECT,
                  fieldName: processSetupKeys.PROCESS_SETUP_QUESTION_1_6_6_B,
                  fieldLabel: 'Route4',
                  dynamicOptionsFromTableA: true,
                  colSpan: true
                },
                {
                  type: inputType.SELECT,
                  fieldName: processSetupKeys.PROCESS_SETUP_QUESTION_1_6_7_B,
                  fieldLabel: 'Route5',
                  dynamicOptionsFromTableA: true,
                  colSpan: true
                },
                {
                  type: inputType.SELECT,
                  fieldName: processSetupKeys.PROCESS_SETUP_QUESTION_1_6_8_B,
                  fieldLabel: 'Route6',
                  dynamicOptionsFromTableA: true,
                  colSpan: true
                },
                {
                  type: inputType.TEXTAREA,
                  fieldName: processSetupKeys.PROCESS_SETUP_QUESTION_1_6_9_B,
                  fieldLabel: 'Custom Name',
                  colSpan: true,
                  isRequired: true
                }
              ]
            }
          }
        ]
      }
    ]
  },
  {
    key: '7',
    label: 'Subsection 1.7 – Precursor Setup',
    items: [
      {
        field: [
          {
            fieldType: inputType.ADD_NEW_TO_TABLE,
            fieldName: precursorSetupKeys.PRECURSOR_SETUP_QUESTION_1_7_1,
            questionNo: '1.7.1',
            fieldLabel: 'Purchased precursors',
            tooltip:
              'List CBAM-relevant purchased inputs (precursors) used in producing the CBAM good. This triggers Section 5 precursor emissions reporting.',
            colSpan: true,
            isRequired: true,
            srNoPrefix: 'PP',
            formList: {
              formFields: [
                {
                  type: inputType.SELECT,
                  fieldName: precursorSetupKeys.PRECURSOR_SETUP_QUESTION_1_7_2,
                  fieldLabel: 'Production process',
                  options: GOODS_CATEGORY_OPTIONS,
                  colSpan: true
                },
                {
                  type: inputType.SELECT,
                  fieldName: precursorSetupKeys.PRECURSOR_SETUP_QUESTION_1_7_3,
                  fieldLabel: 'Country code',
                  options: COUNTRY_WITH_CODE_OPTIONS,
                  colSpan: true
                },
                {
                  type: inputType.SELECT,
                  fieldName: precursorSetupKeys.PRECURSOR_SETUP_QUESTION_1_7_4,
                  fieldLabel: 'Route1',
                  options: ROUTE_OPTIONS,
                  colSpan: true
                },
                {
                  type: inputType.SELECT,
                  fieldName: precursorSetupKeys.PRECURSOR_SETUP_QUESTION_1_7_5,
                  fieldLabel: 'Route2',
                  options: ROUTE_OPTIONS,
                  colSpan: true
                },
                {
                  type: inputType.SELECT,
                  fieldName: precursorSetupKeys.PRECURSOR_SETUP_QUESTION_1_7_6,
                  fieldLabel: 'Route3',
                  options: ROUTE_OPTIONS,
                  colSpan: true
                },
                {
                  type: inputType.SELECT,
                  fieldName: precursorSetupKeys.PRECURSOR_SETUP_QUESTION_1_7_7,
                  fieldLabel: 'Route4',
                  options: ROUTE_OPTIONS,
                  colSpan: true
                },
                {
                  type: inputType.SELECT,
                  fieldName: precursorSetupKeys.PRECURSOR_SETUP_QUESTION_1_7_8,
                  fieldLabel: 'Route5',
                  options: ROUTE_OPTIONS,
                  colSpan: true
                },
                {
                  type: inputType.TEXTAREA,
                  fieldName: precursorSetupKeys.PRECURSOR_SETUP_QUESTION_1_7_9,
                  fieldLabel: 'Custom Name',
                  colSpan: true
                }
              ]
            }
          }
        ]
      }
    ]
  },
  {
    key: '8',
    label: 'Subsection 1.8 – Authorised Representative',
    items: [
      {
        field: [
          {
            fieldType: inputType.TEXT,
            fieldName:
              authorizedRepresentativeKeys.AUTHORIZED_REP_QUESTION_1_8_1,
            questionNo: '1.8.1',
            fieldLabel: 'Authorised Representative Name',
            tooltip:
              'Person legally responsible for CBAM data submission/communication. Must be consistent with company authorization.',
            colSpan: true,
            isRequired: true
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.EMAIL,
            fieldName:
              authorizedRepresentativeKeys.AUTHORIZED_REP_QUESTION_1_8_2,
            questionNo: '1.8.2',
            fieldLabel: 'Authorised Representative Email',
            tooltip:
              'Official email of responsible contact. Use a working business email for CBAM follow-ups.',
            colSpan: true,
            isRequired: true
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.MOBILE,
            fieldName:
              authorizedRepresentativeKeys.AUTHORIZED_REP_QUESTION_1_8_3,
            questionNo: '1.8.3',
            fieldLabel: 'Authorised Representative Phone',
            tooltip:
              'Direct phone number for clarification requests. Ensure it is reachable and includes country code.',
            isRequired: true,
            colSpan: true
          }
        ]
      }
    ]
  },
  {
    key: '9',
    label: 'Subsection 1.9 – Product Compliance',
    items: [
      {
        field: [
          {
            fieldType: inputType.MULTISELECT,
            fieldName: ProductComplianceKeys.PRODUCT_COMPLIANCE_QUESTION_1_9_1,
            questionNo: '1.9.1',
            fieldLabel: 'CN Code',
            tooltip:
              'Select the CN code of the goods exported to EU. Must match customs declaration / invoice CN classification.',
            options: CN_CODE_OPTIONS,
            isRequired: true,
            colSpan: true
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.RADIOGROUP,
            fieldName: ProductComplianceKeys.PRODUCT_COMPLIANCE_QUESTION_1_9_2,
            questionNo: '1.9.2',
            fieldLabel: 'Inward Processing Relief?',
            tooltip:
              'Indicate whether goods are under inward processing regime (EU customs). Impacts compliance interpretation.',
            options: YES_NO_OPTIONS,
            isRequired: true,
            colSpan: true
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.FILEUPLOAD,
            fieldName: ProductComplianceKeys.PRODUCT_COMPLIANCE_QUESTION_1_9_3,
            questionNo: '1.9.3',
            fieldLabel: 'Verification Report (Upload)',
            tooltip:
              'Upload verification report PDF if verification is done (typically required when 1.5.1 = Yes). Keep document traceable and signed.',
            conditionalVisibility: {
              dependsOn: verificationKeys.VERIFICATION_QUESTION_1_5_1,
              showWhen: 'Yes',
              condition: conditionType.EQUALS
            },
            requiredOn: {
              fieldName: verificationKeys.VERIFICATION_QUESTION_1_5_1,
              condition: conditionType.EQUALS,
              values: ['Yes'],
              required: true
            },
            colSpan: true
          }
        ]
      }
    ]
  }
];

// Section  2
export const section2SourceStreamAndDirectEmissionsDataTabQuestions = [
  {
    key: '1',
    label: 'Subsection 2.1 – Source Identification',
    items: [
      {
        field: [
          {
            fieldType: inputType.ADD_NEW_TO_TABLE,
            fieldName: sourceStreamKeys.SOURCE_STREAM_QUESTION_2_1_1,
            questionNo: '2.1.1',
            fieldLabel: 'Source Stream ID',
            tooltip:
              'Provide a unique internal identifier for each source stream (fuel, raw material, waste gas, etc.). Must remain consistent across Sections 3 and 4.',
            colSpan: true,
            isRequired: true,
            formList: {
              formFields: [
                {
                  type: inputType.TEXTAREA,
                  fieldName: sourceStreamKeys.SOURCE_STREAM_QUESTION_2_1_1_1,
                  fieldLabel: 'Source Stream ID',
                  colSpan: true,
                  isRequired: true
                },
                {
                  type: inputType.TEXTAREA,
                  fieldName: sourceStreamKeys.SOURCE_STREAM_QUESTION_2_1_1_2,
                  fieldLabel: 'Source Stream Description',
                  colSpan: true,
                  isRequired: true
                },
                {
                  type: inputType.NUMBER,
                  fieldName: sourceStreamKeys.SOURCE_STREAM_QUESTION_2_1_1_3,
                  fieldLabel: 'Total Consumed',
                  colSpan: true,
                  isRequired: true
                }
              ]
            }
          }
        ]
      }
    ]
  },
  {
    key: '2',
    label: 'Subsection 2.2 – Stream Type & Methodology',
    items: [
      {
        field: [
          {
            fieldType: inputType.SELECT,
            fieldName:
              monitoringMethodologyKeys.MONITORING_METHODOLOGY_QUESTION_2_2_1,
            questionNo: '2.2.1',
            fieldLabel: 'Source Stream Type',
            tooltip:
              'Select the type: Combustion, Process Emission, Mass Balance, etc. Must reflect the emission origin as defined under Annex IV.',
            options: SOURCE_STREAM_TYPE_OPTIONS,
            isRequired: true,
            colSpan: true
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.SELECT,
            fieldName:
              monitoringMethodologyKeys.MONITORING_METHODOLOGY_QUESTION_2_2_2,
            questionNo: '2.2.2',
            fieldLabel: 'Monitoring Methodology',
            tooltip:
              'Select calculation-based or measurement-based method. Must align with Annex IV methodology hierarchy (prefer actual data over defaults).',
            options: MONITORING_METHODOLOGY_OPTIONS,
            isRequired: true,
            colSpan: true
          }
        ]
      }
    ]
  },
  {
    key: '3',
    label: 'Subsection 2.3 – Activity Data',
    items: [
      {
        field: [
          {
            fieldType: inputType.ADD_NEW_TO_TABLE,
            fieldName: activityDataKeys.ACTIVITY_DATA_QUESTION_2_3_1,
            questionNo: '2.3.1',
            fieldLabel: 'Activity Data (Quantity)',
            tooltip:
              'Enter total quantity consumed during reporting period. Must be supported by weighbridge, meter, or stock reconciliation records.',
            colSpan: true,
            isRequired: true,
            allowMultipleRows: false,
            formList: {
              formFields: [
                {
                  type: inputType.TEXT,
                  fieldName: activityDataKeys.ACTIVITY_DATA_QUESTION_2_3_1_1,
                  fieldLabel: 'Activity Type',
                  colSpan: true
                },
                {
                  type: inputType.TEXT,
                  fieldName: activityDataKeys.ACTIVITY_DATA_QUESTION_2_3_1_2,
                  fieldLabel: 'Activity Sub- Type',
                  colSpan: true
                },
                {
                  type: inputType.TEXT,
                  fieldName: activityDataKeys.ACTIVITY_DATA_QUESTION_2_3_1_3,
                  fieldLabel: 'Consumption data type',
                  colSpan: true
                },
                {
                  type: inputType.NUMBER,
                  fieldName: activityDataKeys.ACTIVITY_DATA_QUESTION_2_3_1_4,
                  fieldLabel: 'Total Consume',
                  colSpan: true
                },
                {
                  type: inputType.NUMBER,
                  fieldName: activityDataKeys.ACTIVITY_DATA_QUESTION_2_3_1_5,
                  fieldLabel: 'EF Value',
                  colSpan: true
                },
                {
                  type: inputType.TEXT,
                  fieldName: activityDataKeys.ACTIVITY_DATA_QUESTION_2_3_1_6,
                  fieldLabel: 'Note/Description',
                  colSpan: true
                },
                {
                  type: inputType.NUMBER,
                  fieldName: activityDataKeys.ACTIVITY_DATA_QUESTION_2_3_1_7,
                  fieldLabel: 'Total Emission',
                  colSpan: true
                }
              ]
            }
          }
        ]
      }
    ]
  }
];

// SECTION 3
export const section3ElectricityAndIndirectEmissionsTabQuestions = [
  {
    key: '1',
    label: 'Subsection 3.1 – Fuel Allocation',
    items: [
      {
        field: [
          {
            fieldType: inputType.HEADING,
            heading: '3.1.1 Fuel Input for Electricity',
            tooltip:
              'Describe how fuel consumption is allocated to products/processes when multiple outputs exist. Allocation must reflect physical or energy-based relationship and avoid double counting under Annex IV.'
          },
          {
            fieldType: inputType.ADD_FIXED_ROW_TO_TABLE,
            fieldName: fuelAllocationKeys.FUEL_ALLOCATION_QUESTION_3_1_1,
            fixedRows: [],
            scopeColumns: [
              {
                title: 'Stream ID',
                dataIndex: 'scopeLabel'
              },
              {
                title: 'Fuel Name',
                dataIndex: 'fuelName'
              },
              {
                title: 'Total QTY f(From 2.1.1)',
                dataIndex: 'totalQty'
              }
            ],
            formList: {
              formFields: [
                {
                  type: inputType.NUMBER,
                  fieldName:
                    fuelAllocationKeys.FUEL_ALLOCATION_QUESTION_3_1_1_1,
                  fieldLabel: 'QTY (TJ) used for electricity',
                  colSpan: true,
                  isRequired: true
                }
              ]
            }
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.HEADING,
            heading: '3.1.2 Fuel Input for Non-CBAM',
            tooltip:
              'Briefly explain why the chosen allocation method accurately reflects process reality. Must be technically defensible and consistent across reporting period.'
          },
          {
            fieldType: inputType.ADD_FIXED_ROW_TO_TABLE,
            fieldName: fuelAllocationKeys.FUEL_ALLOCATION_QUESTION_3_1_2,
            fixedRows: [],
            scopeColumns: [
              {
                title: 'Stream ID',
                dataIndex: 'scopeLabel'
              },
              {
                title: 'Fuel Name',
                dataIndex: 'fuelName'
              }
            ],
            formList: {
              formFields: [
                {
                  type: inputType.NUMBER,
                  fieldName:
                    fuelAllocationKeys.FUEL_ALLOCATION_QUESTION_3_1_2_1,
                  fieldLabel: 'QTY (TJ) for non CBAM',
                  colSpan: true,
                  isRequired: true
                }
              ]
            }
          }
        ]
      }
    ]
  },
  {
    key: '2',
    label: 'Subsection 3.2 – Monitoring Approach',
    items: [
      {
        field: [
          {
            fieldType: inputType.SELECT,
            fieldName:
              monitoringApproachKeys.MONITORING_APPROACH_QUESTION_3_2_1,
            questionNo: '3.2.1',
            fieldLabel: 'Predominant Method',
            tooltip:
              'Select whether emissions are calculated using Calculation-Based or Measurement-Based methodology. Must follow Annex IV formula hierarchy.',
            options: PREDOMINANT_METHOD_OPTIONS,
            isRequired: true,
            colSpan: true
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.SELECT,
            fieldName:
              monitoringApproachKeys.MONITORING_APPROACH_QUESTION_3_2_2,
            questionNo: '3.2.2',
            fieldLabel: 'Justification for Default Values',
            tooltip:
              'Indicate whether default values or installation-specific measured data are used. Higher accuracy data preferred in definitive phase.',
            colSpan: true,
            options: JUSTIFICATION_FOR_DEFAULT_VALUES_OPTIONS,
            conditionalVisibility: {
              dependsOn:
                monitoringApproachKeys.MONITORING_APPROACH_QUESTION_3_2_1,
              showWhen: 'Measurement-based',
              condition: conditionType.EQUALS
            },
            requiredOn: {
              fieldName:
                monitoringApproachKeys.MONITORING_APPROACH_QUESTION_3_2_1,
              condition: conditionType.EQUALS,
              values: ['Measurement-based'],
              required: true
            }
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.SELECT,
            fieldName:
              monitoringApproachKeys.MONITORING_APPROACH_QUESTION_3_2_3,
            questionNo: '3.2.3',
            fieldLabel: 'Quality Assurance',
            tooltip:
              'Describe source of NCV, EF, carbon content (lab reports, supplier certificates, regulatory defaults). Must be auditable.',
            options: QUALITY_ASSURANCE_OPTIONS,
            isRequired: true,
            colSpan: true
          }
        ]
      }
    ]
  },
  {
    key: '3',
    label: 'Subsection 3.3 – Electricity',
    items: [
      {
        field: [
          {
            fieldType: inputType.SELECT,
            fieldName: electricityKeys.ELECTRICITY_QUESTION_3_3_1,
            questionNo: '3.3.1',
            fieldLabel: 'Electricity Connection',
            tooltip:
              'Specify whether electricity is grid-supplied or self-generated. Determines indirect emission calculation approach.',
            colSpan: true,
            options: ELECTRICITY_CONNECTION_OPTIONS,
            isRequired: true
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.NUMBER,
            fieldName: electricityKeys.ELECTRICITY_QUESTION_3_3_2,
            questionNo: '3.3.2',
            fieldLabel: 'Total Electricity Imported',
            tooltip:
              'Enter emission factor applied for electricity. Must use country-specific default or verified supplier-specific factor if eligible.',
            roundTo: 2,
            colSpan: true,
            conditionalVisibility: {
              dependsOn: electricityKeys.ELECTRICITY_QUESTION_3_3_1,
              showWhen: ['Grid connection', 'Both'],
              condition: conditionType.EQUALS
            },
            requiredOn: {
              fieldName: electricityKeys.ELECTRICITY_QUESTION_3_3_1,
              condition: conditionType.EQUALS,
              values: ['Grid connection', 'Both'],
              required: true
            }
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.RADIOGROUP,
            fieldName: electricityKeys.ELECTRICITY_QUESTION_3_3_3,
            questionNo: '3.3.3',
            fieldLabel: 'Electricity Exported?',
            tooltip:
              'Explain basis for chosen electricity factor (national default, verified supplier mix, renewable contract).',
            colSpan: true,
            options: YES_NO_OPTIONS,
            isRequired: true
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.NUMBER,
            fieldName: electricityKeys.ELECTRICITY_QUESTION_3_3_4,
            questionNo: '3.3.3.a',
            fieldLabel: 'Total Electricity Exported (MWh)',
            tooltip:
              'Enter emission factor applied for electricity. Must use country-specific default or verified supplier-specific factor if eligible.',
            colSpan: true,
            roundTo: 2,
            conditionalVisibility: {
              dependsOn: electricityKeys.ELECTRICITY_QUESTION_3_3_3,
              showWhen: ['Yes'],
              condition: conditionType.EQUALS
            },
            requiredOn: {
              fieldName: electricityKeys.ELECTRICITY_QUESTION_3_3_3,
              condition: conditionType.EQUALS,
              values: ['Yes'],
              required: true
            }
          }
        ]
      }
    ]
  },
  {
    key: '4',
    label: 'Subsection 3.4 – Emission Factors & CHP',
    items: [
      {
        field: [
          {
            fieldType: inputType.SELECT,
            fieldName:
              emissionFactorsAndCHPKeys.EMISSION_FACTORS_AND_CHP_QUESTION_3_4_1,
            questionNo: '3.4.1',
            fieldLabel: 'Emission Factor Source',
            tooltip:
              'Specify whether emission factors are default values (EU/IPCC) or measured installation-specific values. Must align with Section 2 declarations.',
            options: EMISSION_FACTOR_SOURCE_OPTIONS,
            isRequired: true,
            colSpan: true
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.SELECT,
            fieldName:
              emissionFactorsAndCHPKeys.EMISSION_FACTORS_AND_CHP_QUESTION_3_4_2,
            questionNo: '3.4.2',
            fieldLabel: 'Region / Country',
            tooltip:
              'If Combined Heat & Power is used, describe how emissions are split between heat and electricity output. Must follow efficiency method under Annex IV.',
            colSpan: true,
            options: COUNTRY_OPTIONS,
            conditionalVisibility: {
              dependsOn:
                emissionFactorsAndCHPKeys.EMISSION_FACTORS_AND_CHP_QUESTION_3_4_1,
              showWhen: 'Region Average',
              condition: conditionType.EQUALS
            },
            requiredOn: {
              fieldName:
                emissionFactorsAndCHPKeys.EMISSION_FACTORS_AND_CHP_QUESTION_3_4_1,
              condition: conditionType.EQUALS,
              values: 'Region Average',
              required: true
            }
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.PERCENTAGE,
            fieldName:
              emissionFactorsAndCHPKeys.EMISSION_FACTORS_AND_CHP_QUESTION_3_4_3,
            questionNo: '3.4.3',
            fieldLabel: 'CHP Efficiency (Elec/Heat)',
            tooltip:
              'If using mass balance method, specify carbon content basis and frequency of testing. Required for process emissions or solid fuels.',
            colSpan: true,
            units: Units.percentage,
            conditionalVisibility: {
              dependsOn: electricityKeys.ELECTRICITY_QUESTION_3_3_1,
              showWhen: ['Both'],
              condition: conditionType.EQUALS
            },
            requiredOn: {
              fieldName: electricityKeys.ELECTRICITY_QUESTION_3_3_1,
              condition: conditionType.EQUALS,
              values: ['Both'],
              required: true
            }
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.FILEUPLOAD,
            fieldName:
              emissionFactorsAndCHPKeys.EMISSION_FACTORS_AND_CHP_QUESTION_3_4_4,
            questionNo: '3.4.4',
            fieldLabel: 'PPA Contract / Green Power Certificate Upload',
            tooltip:
              'If using mass balance method, specify carbon content basis and frequency of testing. Required for process emissions or solid fuels.',
            colSpan: true,
            conditionalVisibility: {
              dependsOn:
                emissionFactorsAndCHPKeys.EMISSION_FACTORS_AND_CHP_QUESTION_3_4_1,
              showWhen: 'PPA (Green Power)',
              condition: conditionType.EQUALS
            },
            requiredOn: {
              fieldName:
                emissionFactorsAndCHPKeys.EMISSION_FACTORS_AND_CHP_QUESTION_3_4_1,
              condition: conditionType.EQUALS,
              values: ['PPA (Green Power)'],
              required: true
            }
          }
        ]
      }
    ]
  }
];

// section 4

export const section4ProcessLevelProductionAndEmissionsDataTabQuestions = [
  {
    key: '1',
    label: 'Subsection 4.1 – Process Identification',
    items: [
      {
        field: [
          {
            fieldType: inputType.TEXT,
            fieldName:
              processIdentificationKeys.PROCESS_IDENTIFICATION_QUESTION_4_1_1,
            questionNo: '4.1.1',
            fieldLabel: 'Process Name',
            tooltip:
              'Enter the exact name of the production unit/process line. Must match Section 1.6 setup and internal production records.',
            colSpan: true,
            autoPopulate: true
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.SELECT,
            fieldName:
              processIdentificationKeys.PROCESS_IDENTIFICATION_QUESTION_4_1_2,
            questionNo: '4.1.2',
            fieldLabel: 'CBAM Good Category',
            tooltip:
              'Select the CBAM “Aggregated Good” produced by this process (Annex I/II category). This sets the system boundary for emission attribution.',
            colSpan: true,
            options: CBAM_GOOD_CATEGORY_OPTIONS,
            isRequired: true
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.SELECT,
            fieldName:
              processIdentificationKeys.PROCESS_IDENTIFICATION_QUESTION_4_1_3,
            questionNo: '4.1.3',
            fieldLabel: 'Production Route',
            tooltip:
              'Choose the technology route used (only relevant for steel/pig iron). This drives the correct Annex IV calculation logic.',
            colSpan: true,
            options: PRODUCTION_ROUTE_OPTIONS,
            isRequired: true,
            conditionalVisibility: {
              dependsOn:
                processIdentificationKeys.PROCESS_IDENTIFICATION_QUESTION_4_1_2,
              showWhen: ['Pig Iron', 'Crude Steel'],
              condition: conditionType.EQUALS
            }
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.TEXT,
            fieldName:
              processIdentificationKeys.PROCESS_IDENTIFICATION_QUESTION_4_1_4,
            questionNo: '4.1.4',
            fieldLabel: 'Steel Mill ID Number',
            tooltip:
              'Provide the unique internal ID for this furnace/line/heat system for traceability. Must link consistently across batches/records.',
            colSpan: true,
            isRequired: true
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.RADIOGROUP,
            fieldName:
              processIdentificationKeys.PROCESS_IDENTIFICATION_QUESTION_4_1_5,
            questionNo: '4.1.5',
            fieldLabel: 'Integrated Mill (Bubble Approach)?',
            tooltip:
              'Indicate if emissions are reported as an integrated chain (e.g., sinter+BF+BOF) under a “bubble”. Must be consistent with your monitoring boundary.',
            colSpan: true,
            options: YES_NO_OPTIONS,
            isRequired: true
          }
        ]
      }
    ]
  },
  {
    key: '2',
    label: 'Subsection 4.2 – Chemical Composition',
    items: [
      {
        field: [
          {
            fieldType: inputType.PERCENTAGE,
            fieldName:
              chemicalCompositionKeys.CHEMICAL_COMPOSITION_QUESTION_4_2_1,
            questionNo: '4.2.1',
            fieldLabel: 'Manganese (Mn) Content %',
            tooltip:
              'If alloy steel/pig iron composition is required, enter Mn % based on lab/quality certificate. Use period-average or batch-weighted average.',
            colSpan: true,
            units: Units.percentage,
            isRequired: true
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.PERCENTAGE,
            fieldName:
              chemicalCompositionKeys.CHEMICAL_COMPOSITION_QUESTION_4_2_2,
            questionNo: '4.2.2',
            fieldLabel: 'Chromium (Cr) Content %',
            tooltip:
              'Enter Cr % from verified product chemistry reports. Required when alloy specification impacts classification/composition reporting.',
            colSpan: true,
            units: Units.percentage,
            isRequired: true
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.PERCENTAGE,
            fieldName:
              chemicalCompositionKeys.CHEMICAL_COMPOSITION_QUESTION_4_2_3,
            questionNo: '4.2.3',
            fieldLabel: 'Nickel (Ni) Content %',
            tooltip:
              'Enter Ni % based on test certificates / lab results. Maintain consistency with product grade specification.',
            colSpan: true,
            units: Units.percentage,
            isRequired: true
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.PERCENTAGE,
            fieldName:
              chemicalCompositionKeys.CHEMICAL_COMPOSITION_QUESTION_4_2_4,
            questionNo: '4.2.4',
            fieldLabel: 'Carbon (C) Content %',
            tooltip:
              'Enter product carbon % (chemistry). Use lab-tested values and keep evidence (mill test certificate).',
            colSpan: true,
            units: Units.percentage,
            isRequired: true
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.PERCENTAGE,
            fieldName:
              chemicalCompositionKeys.CHEMICAL_COMPOSITION_QUESTION_4_2_5,
            questionNo: '4.2.5',
            fieldLabel: 'Other Alloys Content %',
            tooltip:
              'Enter combined % of other alloying elements not separately listed. Must be defensible from lab reports.',
            colSpan: true,
            units: Units.percentage,
            isRequired: true
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.RADIOGROUP,
            fieldName:
              chemicalCompositionKeys.CHEMICAL_COMPOSITION_QUESTION_4_2_6,
            questionNo: '4.2.6 a',
            fieldLabel:
              'Does this product contain non-steel/non-metallic structural materials?',
            tooltip:
              'If composite/non-pure steel content exists, declare % of non-metallic content. Keep methodology consistent.',
            colSpan: true,
            options: YES_NO_OPTIONS,
            isRequired: true
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.PERCENTAGE,
            fieldName:
              chemicalCompositionKeys.CHEMICAL_COMPOSITION_QUESTION_4_2_7,
            questionNo: '4.2.6',
            fieldLabel: 'Non-Steel Material Content %',
            tooltip:
              'If composite/non-pure steel content exists, declare % of non-metallic content. Keep methodology consistent.',
            colSpan: true,
            units: Units.percentage,
            conditionalVisibility: {
              dependsOn:
                chemicalCompositionKeys.CHEMICAL_COMPOSITION_QUESTION_4_2_6,
              showWhen: 'Yes',
              condition: conditionType.EQUALS
            }
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.TEXT,
            fieldName:
              chemicalCompositionKeys.CHEMICAL_COMPOSITION_QUESTION_4_2_8,
            questionNo: '4.2.7',
            fieldLabel: 'Steel Mill ID Number',
            tooltip:
              'Repeat/confirm the unique heat/batch ID that ties chemical composition to the process output for audit traceability.',
            colSpan: true,
            isRequired: true
          }
        ]
      }
    ]
  },
  {
    key: '3',
    label: 'Subsection 4.3 – Production',
    items: [
      {
        field: [
          {
            fieldType: inputType.NUMBER,
            fieldName: productionKeys.PRODUCTION_QUESTION_4_3_1,
            questionNo: '4.3.1',
            fieldLabel: 'Total Production (Activity Level)',
            tooltip:
              'Report total production quantity of the CBAM good from this process in the reporting period. This is the denominator for emission intensity (tCO₂/tonne).',
            colSpan: true,
            roundTo: 2,
            isRequired: true
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.RADIOGROUP,
            fieldName: productionKeys.PRODUCTION_QUESTION_4_3_2,
            questionNo: '4.3.2',
            fieldLabel: 'Purchased Precursors Used?',
            tooltip:
              'Select Yes/No if any purchased CBAM precursor materials are used (which triggers Section 5 reporting). Must match procurement reality.',
            colSpan: true,
            options: YES_NO_OPTIONS,
            isRequired: true
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.SELECT,
            fieldName: productionKeys.PRODUCTION_QUESTION_4_3_3,
            questionNo: '4.3.3',
            fieldLabel: 'Main Reducing Agent',
            tooltip:
              'Select the main reducing agent used in the process (relevant for metallurgical routes). Must align with process design and inputs.',
            colSpan: true,
            options: MAIN_REDUCTION_AGENT_OPTIONS,
            isRequired: true
          }
        ]
      }
    ]
  },
  {
    key: '4',
    label: 'Subsection 4.4 – Fuel Inputs',
    items: [
      {
        field: [
          {
            fieldType: inputType.CHECKBOXGROUP,
            fieldName: fuelInputsKeys.FUEL_INPUTS_QUESTION_4_4_1,
            questionNo: '4.4.1',
            fieldLabel: 'Fuel Linkage',
            tooltip:
              'Tick/select the fuels (from Section 2 source streams) that are combusted/used in this process. Must match allocation logic in Section 3.',
            colSpan: true,
            isRequired: true,
            autoPopulate: true
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.NUMBER,
            fieldName: fuelInputsKeys.FUEL_INPUTS_QUESTION_4_4_2,
            questionNo: '4.4.2',
            fieldLabel: 'Fuel Quantity (Attributed)',
            tooltip:
              'Enter the quantity of fuel attributed to this process (after allocation). Must not exceed the total fuel quantity declared in Section 2.3.1.',
            colSpan: true,
            roundTo: 2,
            isRequired: true
          }
        ]
      }
    ]
  },
  {
    key: '5',
    label: 'Subsection 4.5 – Energy Balance',
    items: [
      {
        field: [
          {
            fieldType: inputType.SELECT,
            fieldName: energyBalanceKeys.ENERGY_BALANCE_QUESTION_4_5_1,
            questionNo: '4.5.1',
            fieldLabel: 'Measurable Heat (Steam) Balance',
            tooltip:
              'Indicate if heat/steam is imported/exported or no transfer. CBAM requires correct boundary treatment for imported heat emissions.',
            colSpan: true,
            options: MEASURABLE_HEAT_OPTIONS,
            isRequired: true,
            conditionalVisibility: {
              dependsOn: productionKeys.PRODUCTION_QUESTION_4_3_2,
              showWhen: ['Yes'],
              condition: conditionType.EQUALS
            },
            requiredOn: {
              fieldName: productionKeys.PRODUCTION_QUESTION_4_3_2,
              condition: conditionType.EQUALS,
              values: ['Yes'],
              required: true
            }
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.NUMBER,
            fieldName: energyBalanceKeys.ENERGY_BALANCE_QUESTION_4_5_2,
            questionNo: '4.5.2',
            fieldLabel: 'Net Heat Quantity',
            tooltip:
              'If heat is imported/exported, enter net heat quantity (energy basis) consistent with metering/steam balance.',
            colSpan: true,
            roundTo: 2,
            conditionalVisibility: {
              dependsOn: energyBalanceKeys.ENERGY_BALANCE_QUESTION_4_5_1,
              showWhen: ['Heat Imported', 'Heat Exported'],
              condition: conditionType.EQUALS
            }
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.NUMBER,
            fieldName: energyBalanceKeys.ENERGY_BALANCE_QUESTION_4_5_3,
            questionNo: '4.5.3',
            fieldLabel: 'Imported Heat Emission Factor',
            tooltip:
              'If heat is imported, provide the emission factor of imported heat (supplier/verified data or accepted defaults). Must be document-backed.',
            colSpan: true,
            roundTo: 2,
            conditionalVisibility: {
              dependsOn: energyBalanceKeys.ENERGY_BALANCE_QUESTION_4_5_1,
              showWhen: 'Heat Imported',
              condition: conditionType.EQUALS
            },
            requiredOn: {
              fieldName: energyBalanceKeys.ENERGY_BALANCE_QUESTION_4_5_1,
              condition: conditionType.EQUALS,
              values: ['Heat Imported'],
              required: true
            }
          }
        ]
      }
    ]
  },
  {
    key: '6',
    label: 'Subsection 4.6 – Waste Gas',
    items: [
      {
        field: [
          {
            fieldType: inputType.RADIOGROUP,
            fieldName: wasteGasKeys.WASTE_GAS_QUESTION_4_6_1,
            questionNo: '4.6.1',
            fieldLabel: 'Waste Gas Generated?',
            tooltip:
              'Indicate if the process generates waste gases (BF gas/COG/BOF gas). Critical to avoid double counting under Annex IV rules.',
            colSpan: true,
            options: YES_NO_OPTIONS,
            isRequired: true
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.SELECT,
            fieldName: wasteGasKeys.WASTE_GAS_QUESTION_4_6_2,
            questionNo: '4.6.2',
            fieldLabel: 'Waste Gas Usage Method',
            tooltip:
              'If waste gas is generated, specify whether it is recycled internally, sold/exported, or flared. Allocation must follow CBAM boundary rules.',
            colSpan: true,
            options: WASTE_GAS_USAGE_METHOD_OPTIONS,
            conditionalVisibility: {
              dependsOn: wasteGasKeys.WASTE_GAS_QUESTION_4_6_1,
              showWhen: 'Yes',
              condition: conditionType.EQUALS
            },
            requiredOn: {
              fieldName: wasteGasKeys.WASTE_GAS_QUESTION_4_6_1,
              condition: conditionType.EQUALS,
              values: ['Yes'],
              required: true
            }
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.NUMBER,
            fieldName: wasteGasKeys.WASTE_GAS_QUESTION_4_6_3,
            questionNo: '4.6.3',
            fieldLabel: 'Waste Gas Volume',
            tooltip:
              'Enter total waste gas produced (in the unit used in your system—energy or volume) for the reporting period. Must be traceable.',
            colSpan: true,
            roundTo: 2,
            conditionalVisibility: {
              dependsOn: wasteGasKeys.WASTE_GAS_QUESTION_4_6_1,
              showWhen: 'Yes',
              condition: conditionType.EQUALS
            },
            requiredOn: {
              fieldName: wasteGasKeys.WASTE_GAS_QUESTION_4_6_1,
              condition: conditionType.EQUALS,
              values: ['Yes'],
              required: true
            }
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.RADIOGROUP,
            fieldName: wasteGasKeys.WASTE_GAS_QUESTION_4_6_4,
            questionNo: '4.6.4',
            fieldLabel: 'Waste Gas Consumed?',
            tooltip:
              'Indicate whether this process consumes waste gas generated elsewhere. Important to track transfers and prevent double counting.',
            colSpan: true,
            options: YES_NO_OPTIONS,
            isRequired: true
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.RADIOGROUP,
            fieldName: wasteGasKeys.WASTE_GAS_QUESTION_4_6_5,
            questionNo: '4.6.5',
            fieldLabel: 'Waste Gas Flared?',
            tooltip:
              'Indicate if waste gas is flared without energy recovery. Flaring still counts as emissions and must be included in totals.',
            colSpan: true,
            options: YES_NO_OPTIONS,
            isRequired: true
          }
        ]
      }
    ]
  },
  {
    key: '7',
    label: 'Subsection 4.7 – Scrap & Electricity',
    items: [
      {
        field: [
          {
            fieldType: inputType.PERCENTAGE,
            fieldName:
              scrapAndElectricityKeys.SCRAP_AND_ELECTRICITY_QUESTION_4_7_1,
            questionNo: '4.7.1',
            fieldLabel: 'Pre-consumer Scrap %',
            tooltip:
              'Enter % of internal (pre-consumer) scrap recycled within operations. Must be consistent with production and scrap accounting.',
            colSpan: true,
            units: Units.percentage,
            isRequired: true
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.PERCENTAGE,
            fieldName:
              scrapAndElectricityKeys.SCRAP_AND_ELECTRICITY_QUESTION_4_7_2,
            questionNo: '4.7.2',
            fieldLabel: 'Post-consumer Scrap %',
            tooltip:
              'Enter % of post-consumer scrap used (external scrap). Important for routes like EAF where scrap affects embedded emissions intensity.',
            colSpan: true,
            units: Units.percentage,
            isRequired: true
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.PERCENTAGE,
            fieldName:
              scrapAndElectricityKeys.SCRAP_AND_ELECTRICITY_QUESTION_4_7_3,
            questionNo: '4.7.3',
            fieldLabel: 'Electricity Consumed',
            tooltip:
              'Enter electricity consumed by this process for the reporting period. Must match meter/sub-meter or allocation method and feed indirect emissions.',
            colSpan: true,
            units: Units.percentage,
            isRequired: true
          }
        ]
      }
    ]
  },
  {
    key: '8',
    label: 'Subsection 4.8 – Carbon Capture',
    items: [
      {
        field: [
          {
            fieldType: inputType.NUMBER,
            fieldName: carbonCaptureKeys.CARBON_CAPTURE_QUESTION_4_8_1,
            questionNo: '4.8.1',
            fieldLabel: 'CO₂ Captured / Transferred',
            tooltip: 'Main economic activity code (NACE or equivalent).',
            colSpan: true,
            roundTo: 2,
            errorMessage:
              'Captured CO₂ will be deducted from total process emissions; ensure transfer documentation is available',
            errorMessageCondition: {
              condition: conditionType.GREATER_THAN,
              numericValue: 0
            }
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.TEXT,
            fieldName: carbonCaptureKeys.CARBON_CAPTURE_QUESTION_4_8_2,
            questionNo: '4.8.2',
            fieldLabel: 'Purchaser Installation ID',
            tooltip: 'Main economic activity code (NACE or equivalent).',
            colSpan: true,
            conditionalVisibility: {
              dependsOn: carbonCaptureKeys.CARBON_CAPTURE_QUESTION_4_8_1,
              condition: conditionType.GREATER_THAN,
              numericValue: 0
            },
            requiredOn: {
              fieldName: carbonCaptureKeys.CARBON_CAPTURE_QUESTION_4_8_1,
              condition: conditionType.GREATER_THAN,
              numericValue: 0,
              required: true
            }
          }
        ]
      }
    ]
  }
];

// section 5

export const section5PrecursorPurchasedInputEmissionsDataTabQuestions = [
  {
    key: '1',
    label: 'Subsection 5.1 – Precursor Identification',
    items: [
      {
        field: [
          {
            fieldType: inputType.WYSIWYG,
            fieldName:
              precursorIdentificationKeys.PRECURSOR_IDENTIFICATION_QUESTION_5_1_1,
            questionNo: '5.1.1',
            fieldLabel: 'Precursor Name',
            tooltip:
              'Enter the exact name of the purchased CBAM material used as input. Must match procurement invoices and supplier documentation.',
            colSpan: true,
            isRequired: true
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.SELECT,
            fieldName:
              precursorIdentificationKeys.PRECURSOR_IDENTIFICATION_QUESTION_5_1_2,
            questionNo: '5.1.2',
            fieldLabel: 'Precursor CN Code',
            tooltip:
              'Select the 8-digit CN code of the purchased CBAM good. Must match customs import documentation and tariff classification.',
            colSpan: true,
            options: CN_CODE_OPTIONS,
            isRequired: true
          }
        ]
      }
    ]
  },
  {
    key: '2',
    label: 'Subsection 5.2 – Origin',
    items: [
      {
        field: [
          {
            fieldType: inputType.SELECT,
            fieldName: originKeys.ORIGIN_QUESTION_5_2_1,
            questionNo: '5.2.1',
            fieldLabel: 'Production Route',
            tooltip:
              'Indicate how the supplier manufactured this precursor (e.g., EAF, smelting reduction, calcination). Needed to assess emission intensity credibility.',
            colSpan: true,
            options: PRODUCTION_ROUTE_OPTIONS,
            isRequired: true
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.SELECT,
            fieldName: originKeys.ORIGIN_QUESTION_5_2_2,
            questionNo: '5.2.2',
            fieldLabel: 'Country of Origin',
            tooltip:
              'Select ISO 2-letter country code where the precursor was produced. Impacts indirect emission factors and traceability.',
            colSpan: true,
            options: COUNTRY_OPTIONS,
            isRequired: true
          }
        ]
      }
    ]
  },
  {
    key: '3',
    label: 'Subsection 5.3 – Emissions',
    items: [
      {
        field: [
          {
            fieldType: inputType.NUMBER,
            fieldName: emissionsKeys.EMISSIONS_QUESTION_5_3_1,
            questionNo: '5.3.1',
            fieldLabel: 'Quantity Purchased',
            tooltip:
              'Enter total quantity of the precursor purchased and used during the reporting period. Must reconcile with purchase and inventory records.',
            colSpan: true,
            roundTo: 2,
            isRequired: true
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.NUMBER,
            fieldName: emissionsKeys.EMISSIONS_QUESTION_5_3_2,
            questionNo: '5.3.2',
            fieldLabel: 'Specific Direct Emissions',
            tooltip:
              'Enter supplier-declared direct emissions per tonne of the precursor (tCO₂/t). Must be supported by verifiable documentation.',
            colSpan: true,
            roundTo: 2,
            isRequired: true
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.NUMBER,
            fieldName: emissionsKeys.EMISSIONS_QUESTION_5_3_3,
            questionNo: '5.3.3',
            fieldLabel: 'Specific Indirect Emissions',
            tooltip:
              'Enter electricity-related emissions per tonne declared by supplier. Must follow CBAM indirect emission calculation rules..',
            colSpan: true,
            roundTo: 2,
            isRequired: true
          }
        ]
      }
    ]
  },
  {
    key: '4',
    label: 'Subsection 5.4 – Data Verification',
    items: [
      {
        field: [
          {
            fieldType: inputType.SELECT,
            fieldName: dataVerificationKeys.DATA_VERIFICATION_QUESTION_5_4_1,
            questionNo: '5.4.1',
            fieldLabel: 'Data Source',
            tooltip:
              'Indicate whether emission data is Actual (supplier-provided verified data) or Default. Default values may result in higher CBAM exposure.',
            colSpan: true,
            options: DATA_SOURCE_OPTIONS,
            isRequired: true
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.CHECKBOXGROUP,
            fieldName: dataVerificationKeys.DATA_VERIFICATION_QUESTION_5_4_2,
            questionNo: '5.4.2',
            fieldLabel: 'Link to Process',
            tooltip:
              'Select the process(es) (from Section 4) where this precursor is used. Ensures correct allocation of embedded emissions.',
            colSpan: true,
            isRequired: true,
            autoPopulate: true
          }
        ]
      }
    ]
  }
];

// section 6

export const section6CarbonPricingAndTaxAdjustmentTabQuestions = [
  {
    key: '1',
    label: 'Subsection 6.1 – Carbon Tax Status',
    items: [
      {
        field: [
          {
            fieldType: inputType.RADIOGROUP,
            fieldName: carbonTaxStatusKeys.CARBON_TAX_STATUS_QUESTION_6_1_1,
            questionNo: '6.1.1',
            fieldLabel: 'Carbon Price Paid?',
            tooltip:
              'Select Yes/No to confirm whether a carbon tax, ETS, or equivalent carbon pricing mechanism was paid in the country of production. Only actual payments qualify for CBAM deduction.',
            colSpan: true,
            options: YES_NO_OPTIONS,
            isRequired: true
          }
        ]
      }
    ]
  },
  {
    key: '2',
    label: 'Subsection 6.2 – Tax Details',
    items: [
      {
        field: [
          {
            fieldType: inputType.WYSIWYG,
            fieldName: taxDetailsKeys.TAX_DETAILS_QUESTION_6_2_1,
            questionNo: '6.2.1',
            fieldLabel: 'Legal Reference',
            tooltip:
              'If 6.1.1 = Yes, provide the official name of the law/regulation under which the carbon price was paid. Must be legally enforceable.',
            colSpan: true,
            conditionalVisibility: {
              dependsOn: carbonTaxStatusKeys.CARBON_TAX_STATUS_QUESTION_6_1_1,
              showWhen: 'Yes',
              condition: conditionType.EQUALS
            }
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.SELECT,
            fieldName: taxDetailsKeys.TAX_DETAILS_QUESTION_6_2_2,
            questionNo: '6.2.2',
            fieldLabel: 'Country of Pricing',
            tooltip:
              'Select the country that imposed and collected the carbon price. Must match installation location.',
            colSpan: true,
            options: COUNTRY_OPTIONS,
            conditionalVisibility: {
              dependsOn: carbonTaxStatusKeys.CARBON_TAX_STATUS_QUESTION_6_1_1,
              showWhen: 'Yes',
              condition: conditionType.EQUALS
            }
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.SELECT,
            fieldName: taxDetailsKeys.TAX_DETAILS_QUESTION_6_2_3,
            questionNo: '6.2.3',
            fieldLabel: 'Form of Price',
            tooltip:
              'Indicate whether pricing was via Carbon Tax, ETS (Emission Trading Scheme), or certificate purchase. Must reflect actual compliance mechanism.',
            colSpan: true,
            options: FORM_OF_PRICE_OPTIONS,
            conditionalVisibility: {
              dependsOn: carbonTaxStatusKeys.CARBON_TAX_STATUS_QUESTION_6_1_1,
              showWhen: 'Yes',
              condition: conditionType.EQUALS
            }
          }
        ]
      }
    ]
  },
  {
    key: '3',
    label: 'Subsection 6.3 – Payment',
    items: [
      {
        field: [
          {
            fieldType: inputType.SELECT,
            fieldName: paymentKeys.PAYMENT_QUESTION_6_3_2,
            questionNo: '6.3.1',
            fieldLabel: 'Currency',
            tooltip:
              'Select the currency in which the carbon price was paid. Required for conversion into EUR under CBAM rules.',
            colSpan: true,
            options: CURRENCY_OPTIONS,
            conditionalVisibility: {
              dependsOn: carbonTaxStatusKeys.CARBON_TAX_STATUS_QUESTION_6_1_1,
              showWhen: 'Yes',
              condition: conditionType.EQUALS
            }
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.NUMBER,
            fieldName: paymentKeys.PAYMENT_QUESTION_6_3_1,
            questionNo: '6.3.2',
            fieldLabel: 'Total Amount Paid',
            tooltip:
              'Enter the total monetary amount paid for carbon pricing during the reporting period. Must be supported by receipts or compliance statements.',
            colSpan: true,
            roundTo: 2,
            conditionalVisibility: {
              dependsOn: carbonTaxStatusKeys.CARBON_TAX_STATUS_QUESTION_6_1_1,
              showWhen: 'Yes',
              condition: conditionType.EQUALS
            }
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.NUMBER,
            fieldName: paymentKeys.PAYMENT_QUESTION_6_3_3,
            questionNo: '6.3.3',
            fieldLabel: 'Exchange Rate',
            tooltip:
              'Enter exchange rate used to convert local currency to Euro. Must be consistent with official ECB or customs exchange reference.',
            colSpan: true,
            roundTo: 2,
            conditionalVisibility: {
              dependsOn: carbonTaxStatusKeys.CARBON_TAX_STATUS_QUESTION_6_1_1,
              showWhen: 'Yes',
              condition: conditionType.EQUALS
            }
          }
        ]
      }
    ]
  },
  {
    key: '4',
    label: 'Subsection 6.4 – Coverage & Proof',
    items: [
      {
        field: [
          {
            fieldType: inputType.NUMBER,
            fieldName: coverageAndProofKeys.COVERAGE_AND_PROOF_QUESTION_6_4_1,
            questionNo: '6.4.1',
            fieldLabel: 'Quantity of Emissions Covered',
            tooltip:
              'Enter the number of tonnes of CO₂ for which carbon price was actually paid. Cannot exceed total direct emissions declared.',
            colSpan: true,
            roundTo: 2,
            conditionalVisibility: {
              dependsOn: carbonTaxStatusKeys.CARBON_TAX_STATUS_QUESTION_6_1_1,
              showWhen: 'Yes',
              condition: conditionType.EQUALS
            },
            requiredOn: {
              fieldName: carbonTaxStatusKeys.CARBON_TAX_STATUS_QUESTION_6_1_1,
              condition: conditionType.EQUALS,
              values: ['Yes'],
              required: true
            }
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.PERCENTAGE,
            fieldName: coverageAndProofKeys.COVERAGE_AND_PROOF_QUESTION_6_4_2,
            questionNo: '6.4.2',
            fieldLabel: 'Rebate Eligibility %',
            tooltip:
              'If rebates, free allowances, or exemptions apply, declare the percentage of emissions effectively taxed. Only net paid portion is deductible.',
            colSpan: true,
            units: Units.percentage,
            conditionalVisibility: {
              dependsOn: carbonTaxStatusKeys.CARBON_TAX_STATUS_QUESTION_6_1_1,
              showWhen: 'Yes',
              condition: conditionType.EQUALS
            },
            requiredOn: {
              fieldName: carbonTaxStatusKeys.CARBON_TAX_STATUS_QUESTION_6_1_1,
              condition: conditionType.EQUALS,
              values: ['Yes'],
              required: true
            }
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.FILEUPLOAD,
            fieldName: coverageAndProofKeys.COVERAGE_AND_PROOF_QUESTION_6_4_3,
            questionNo: '6.4.3',
            fieldLabel: 'Proof of Payment (Upload)',
            tooltip:
              'Upload official tax receipt, ETS surrender confirmation, or government certificate proving payment. Required for CBAM deduction validation.',
            colSpan: true,
            conditionalVisibility: {
              dependsOn: carbonTaxStatusKeys.CARBON_TAX_STATUS_QUESTION_6_1_1,
              showWhen: 'Yes',
              condition: conditionType.EQUALS
            },
            requiredOn: {
              fieldName: carbonTaxStatusKeys.CARBON_TAX_STATUS_QUESTION_6_1_1,
              condition: conditionType.EQUALS,
              values: ['Yes'],
              required: true
            }
          }
        ]
      }
    ]
  }
];

// section 7

export const section7ShipmentAndCustomsInformationTabQuestions = [
  {
    key: '1',
    label: 'Subsection 7.1 – Shipment Identification',
    items: [
      {
        field: [
          {
            fieldType: inputType.WYSIWYG,
            fieldName: customerMRNNumberKeys.CUSTOMER_MRN_QUESTION_7_1_1,
            questionNo: '7.1.1',
            fieldLabel: 'Customs MRN Number',
            tooltip:
              'Enter the EU-issued Movement Reference Number (MRN) assigned at EU customs clearance. This links the shipment to EU import records. If not yet available, update once assigned.',
            colSpan: true,
            textLimit: 18
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.ALPHANUMERIC,
            fieldName: customerMRNNumberKeys.CUSTOMER_MRN_QUESTION_7_1_2,
            questionNo: '7.1.2',
            fieldLabel: 'Shipment ID / Invoice No',
            tooltip:
              'Provide your internal export invoice number or shipment identifier. Must match commercial invoice and customs declaration.',
            colSpan: true,
            isRequired: true
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.WYSIWYG,
            fieldName: customerMRNNumberKeys.CUSTOMER_MRN_QUESTION_7_1_3,
            questionNo: '7.1.3',
            fieldLabel: 'EU Importer EORI ',
            tooltip:
              'EORI of the EU importer who will declare your goods at EU customs. Optional — useful if supplying multiple EU buyers.',
            colSpan: true
          }
        ]
      }
    ]
  },
  {
    key: '2',
    label: 'Subsection 7.2 – Logistics',
    items: [
      {
        field: [
          {
            fieldType: inputType.TEXT,
            fieldName: customerLogisticsKeys.LOGISTICS_QUESTION_7_2_1,
            questionNo: '7.2.1',
            fieldLabel: 'Port of Loading (UNLOCODE)',
            tooltip:
              'Enter the port where goods were exported from the country of origin. Should match Bill of Lading and customs export documentation.',
            colSpan: true,
            isRequired: true
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.TEXT,
            fieldName: customerLogisticsKeys.LOGISTICS_QUESTION_7_2_2,
            questionNo: '7.2.2',
            fieldLabel: 'Port of Destination (UNLOCODE)',
            tooltip:
              'Enter the EU port where goods are imported. Must match EU customs declaration and MRN details.',
            colSpan: true,
            isRequired: true
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.TEXT,
            fieldName: customerLogisticsKeys.LOGISTICS_QUESTION_7_2_3,
            questionNo: '7.2.3',
            fieldLabel: 'Container Number',
            tooltip:
              'Provide container identification number (if applicable). Supports logistics traceability but may be optional.',
            colSpan: true
          }
        ]
      }
    ]
  }
];
